import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
} from "antd";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import { getDate } from "../../helper/helper";
import {
  GetPendingQueryCount,
  ListAiPastSessions,
  ListLearnerQueries,
  ReplyLearnerQuery,
  UpdateQueryVisibility,
} from "../../redux/actions/AiPastSessionsAction";

const PAGE_SIZE = 10;

const getId = (item) => item?._id ?? item?.id;

const pick = (obj, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((acc, part) => acc?.[part], obj);
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return undefined;
};

const asUrlList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const LearnerQueries = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [queries, setQueries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [sessionId, setSessionId] = useState();
  const [sessions, setSessions] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [visibilityId, setVisibilityId] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [mediaUrls, setMediaUrls] = useState([""]);

  const fetchPendingCount = useCallback(() => {
    dispatch(
      GetPendingQueryCount(
        (count) => setPendingCount(count),
        () => setPendingCount(0)
      )
    );
  }, [dispatch]);

  const fetchQueries = useCallback(
    (page = 1) => {
      setLoading(true);
      const payload = {
        page,
        limit: PAGE_SIZE,
        ...(sessionId ? { sessionId } : {}),
        ...(statusFilter === "pending"
          ? { isReplied: false }
          : statusFilter === "replied"
          ? { isReplied: true }
          : {}),
      };
      dispatch(
        ListLearnerQueries(
          payload,
          ({ list, pagination }) => {
            const safeList = Array.isArray(list) ? list : [];
            setQueries(safeList);
            setTotalItems(
              pagination?.totalItems ?? pagination?.total ?? safeList.length
            );
            setCurrentPage(page);
            setLoading(false);
          },
          (errMsg) => {
            setLoading(false);
            Modal.error({
              title: "Failed to fetch queries",
              content: errMsg,
            });
          }
        )
      );
    },
    [dispatch, sessionId, statusFilter]
  );

  useEffect(() => {
    fetchQueries(1);
  }, [fetchQueries]);

  useEffect(() => {
    fetchPendingCount();
    dispatch(
      ListAiPastSessions(
        { page: 1, limit: 200 },
        ({ list }) => setSessions(Array.isArray(list) ? list : []),
        () => setSessions([])
      )
    );
  }, [dispatch, fetchPendingCount]);

  const sessionTitleById = useMemo(() => {
    const map = {};
    (sessions ?? []).forEach((session) => {
      const id = getId(session);
      if (id) map[String(id)] = session?.title ?? session?.name ?? String(id);
    });
    return map;
  }, [sessions]);

  const sessionOptions = useMemo(
    () =>
      (sessions ?? []).map((session) => ({
        label: session?.title ?? session?.name ?? String(getId(session)),
        value: String(getId(session)),
      })),
    [sessions]
  );

  const openReply = (record) => {
    setReplyTarget(record);
    setReplyText(record?.replyText && record.replyText !== "-" ? record.replyText : "");
    const existing = record?.replyMediaUrls?.length ? record.replyMediaUrls : [""];
    setMediaUrls(existing);
  };

  const closeReply = () => {
    setReplyTarget(null);
    setReplyText("");
    setMediaUrls([""]);
  };

  const submitReply = () => {
    const text = replyText.trim();
    const urls = mediaUrls.map((url) => url.trim()).filter(Boolean);
    if (!text && !urls.length) {
      Modal.warning({
        title: "Add a reply",
        content: "Enter reply text or at least one media URL.",
      });
      return;
    }

    setSubmitting(true);
    dispatch(
      ReplyLearnerQuery(
        {
          queryId: replyTarget._id,
          ...(text ? { replyText: text } : {}),
          ...(urls.length ? { replyMediaUrls: urls } : {}),
        },
        () => {
          setSubmitting(false);
          closeReply();
          fetchQueries(currentPage);
          fetchPendingCount();
          Modal.success({ title: "Reply sent" });
        },
        (errMsg) => {
          setSubmitting(false);
          Modal.error({ title: "Reply failed", content: errMsg });
        }
      )
    );
  };

  const tableData = useMemo(() => {
    const offset = (currentPage - 1) * PAGE_SIZE;
    return (queries ?? []).map((item, index) => {
      const id = getId(item) ?? String(index);
      const session = pick(item, ["sessionId", "session._id", "session.id"]);
      const sessionKey =
        session && typeof session === "object"
          ? String(getId(session) ?? "")
          : session
          ? String(session)
          : "";
      const replied =
        item?.isReplied === true ||
        Boolean(pick(item, ["replyText", "reply", "adminReply"]));
      return {
        key: id,
        _id: id,
        slNo: offset + index + 1,
        createdAt: item?.createdAt ? getDate(item.createdAt) : "-",
        learner:
          pick(item, [
            "learnerName",
            "learner.name",
            "user.name",
            "userName",
            "askedBy.name",
            "name",
          ]) ?? "-",
        learnerContact:
          pick(item, [
            "learner.email",
            "email",
            "learner.phoneNumber",
            "phoneNumber",
          ]) ?? "",
        session:
          pick(item, [
            "sessionTitle",
            "session.title",
            "sessionName",
            "aiPastSession.title",
          ]) ??
          sessionTitleById[sessionKey] ??
          sessionKey ??
          "-",
        question:
          pick(item, [
            "queryText",
            "question",
            "text",
            "message",
            "query",
            "content",
          ]) ?? "-",
        queryMedia: asUrlList(
          pick(item, ["queryMediaUrls", "mediaUrls", "attachments", "imageUrls"])
        ),
        isReplied: replied,
        replyText: pick(item, ["replyText", "reply", "adminReply"]) ?? "-",
        replyMediaUrls: asUrlList(item?.replyMediaUrls),
        isPublic: item?.isPublic === true,
        votes: Array.isArray(item?.upvotes)
          ? item.upvotes.length
          : Array.isArray(item?.upvote)
          ? item.upvote.length
          : pick(item, ["voteCount", "votes", "upVotes", "upvoteCount"]) ?? 0,
      };
    });
  }, [queries, currentPage, sessionTitleById]);

  const columns = [
    { title: "Sl No", dataIndex: "slNo", key: "slNo", width: 70 },
    { title: "Date", dataIndex: "createdAt", key: "createdAt", width: 120 },
    {
      title: "Learner",
      dataIndex: "learner",
      key: "learner",
      width: 180,
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.learnerContact ? (
            <div style={{ color: "#888", fontSize: 12 }}>{record.learnerContact}</div>
          ) : null}
        </div>
      ),
    },
    { title: "Session", dataIndex: "session", key: "session", width: 200 },
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      width: 280,
      render: (text, record) => (
        <div>
          <span title={text}>{text}</span>
          {record.queryMedia?.length ? (
            <div>
              {record.queryMedia.map((url, idx) => (
                <div key={`${url}-${idx}`}>
                  <a href={url} target="_blank" rel="noreferrer">
                    Attachment {idx + 1}
                  </a>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Votes",
      dataIndex: "votes",
      key: "votes",
      width: 80,
    },
    {
      title: "Visibility",
      dataIndex: "isPublic",
      key: "isPublic",
      width: 120,
      render: (isPublic, record) => (
        <Switch
          checked={isPublic}
          checkedChildren="Public"
          unCheckedChildren="Private"
          loading={visibilityId === record._id}
          onChange={(checked) => {
            setVisibilityId(record._id);
            dispatch(
              UpdateQueryVisibility(
                record._id,
                checked,
                () => {
                  setQueries((current) =>
                    current.map((item) =>
                      String(getId(item)) === String(record._id)
                        ? { ...item, isPublic: checked }
                        : item
                    )
                  );
                  setVisibilityId(null);
                },
                (errMsg) => {
                  setVisibilityId(null);
                  Modal.error({
                    title: "Could not update visibility",
                    content: errMsg,
                  });
                }
              )
            );
          }}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "isReplied",
      key: "isReplied",
      width: 110,
      render: (isReplied) =>
        isReplied ? <Tag color="green">Replied</Tag> : <Tag color="gold">Pending</Tag>,
    },
    {
      title: "Reply",
      dataIndex: "replyText",
      key: "replyText",
      width: 240,
      render: (text, record) => (
        <div>
          <span title={text}>{text}</span>
          {record.replyMediaUrls?.length ? (
            <div>
              {record.replyMediaUrls.map((url, idx) => (
                <div key={`${url}-${idx}`}>
                  <a href={url} target="_blank" rel="noreferrer">
                    Media {idx + 1}
                  </a>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 110,
      render: (_, record) => (
        <Button type="primary" onClick={() => openReply(record)}>
          {record.isReplied ? "Edit reply" : "Reply"}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb route={["dashboard", "Learner Queries"]} />

      <Card
        title={
          <Space>
            <span>Learner Queries</span>
            <Badge count={pendingCount} overflowCount={999} />
          </Space>
        }
      >
        <Space
          size={16}
          style={{ display: "flex", flexWrap: "wrap", marginBottom: 16 }}
          align="end"
        >
          <div>
            <div style={{ marginBottom: 6 }}>Status</div>
            <Select
              style={{ minWidth: 180 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: "Pending", value: "pending" },
                { label: "Replied", value: "replied" },
                { label: "All", value: "all" },
              ]}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Session</div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All sessions"
              style={{ minWidth: 280 }}
              value={sessionId}
              options={sessionOptions}
              onChange={(value) => setSessionId(value)}
            />
          </div>
        </Space>

        {loading ? (
          <div className="spin-center">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={tableData}
            columns={columns}
            scroll={{ x: 1520 }}
            pagination={{
              current: currentPage,
              pageSize: PAGE_SIZE,
              total: totalItems,
              showSizeChanger: false,
              showTotal: (total, range) =>
                total > 0
                  ? `${range[0]}-${range[1]} of ${total} queries`
                  : "0 queries",
              onChange: (page) => fetchQueries(page),
            }}
          />
        )}
      </Card>

      <Modal
        title={replyTarget?.isReplied ? "Edit reply" : "Reply to query"}
        visible={Boolean(replyTarget)}
        onCancel={closeReply}
        onOk={submitReply}
        confirmLoading={submitting}
        okText="Send reply"
        destroyOnClose
      >
        {replyTarget ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Question</div>
              <div>{replyTarget.question}</div>
            </div>
            <div>
              <div style={{ marginBottom: 6 }}>Reply text</div>
              <Input.TextArea
                rows={4}
                value={replyText}
                onChange={(event) => setReplyText(event.target.value)}
                placeholder="Write a reply"
              />
            </div>
            <div>
              <div style={{ marginBottom: 6 }}>Reply media URLs</div>
              {mediaUrls.map((url, index) => (
                <Space
                  key={index}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Input
                    value={url}
                    placeholder="https://s3.aws.com/reply-video.mp4"
                    onChange={(event) => {
                      const next = [...mediaUrls];
                      next[index] = event.target.value;
                      setMediaUrls(next);
                    }}
                    style={{ width: 320 }}
                  />
                  <Button
                    danger
                    disabled={mediaUrls.length <= 1}
                    onClick={() =>
                      setMediaUrls(mediaUrls.filter((_, itemIndex) => itemIndex !== index))
                    }
                  >
                    Remove
                  </Button>
                </Space>
              ))}
              <Button type="dashed" onClick={() => setMediaUrls([...mediaUrls, ""])}>
                Add media URL
              </Button>
            </div>
          </Space>
        ) : null}
      </Modal>
    </div>
  );
};

export default LearnerQueries;
